import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentAltLines, faFileExport } from '@fortawesome/pro-light-svg-icons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

const QUESTION_TEXT_SX = {
  fontSize: { xs: '1rem', md: '1rem' },
  lineHeight: 1.35,
  color: '#334155',
};

const OpenTextEmptyState = ({ message }) => (
  <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 2.2, backgroundColor: '#ffffff', p: 3 }}>
    <Stack alignItems="center" justifyContent="center" spacing={1.4} sx={{ minHeight: 136, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
        }}
      >
        <FontAwesomeIcon icon={faCommentAltLines} style={{ fontSize: 28, color: '#94a3b8' }} />
      </Box>
      <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, color: '#64748b' }}>
        {message}
      </Typography>
    </Stack>
  </Box>
);

OpenTextEmptyState.propTypes = {
  message: PropTypes.string,
};

OpenTextEmptyState.defaultProps = {
  message: 'No responses found for the selected filters.',
};

const ResponseList = ({ responses }) => {
  if (responses.length === 0) {
    return (
      <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, color: '#64748b' }}>
        No responses for this question with the selected filters.
      </Typography>
    );
  }

  return responses.map((response, index) => (
    <Box
      key={`response-${index}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        backgroundColor: '#f8fafc',
        borderRadius: 2,
        px: 2.5,
        py: 2,
        mb: index === responses.length - 1 ? 0 : 1,
      }}
    >
      <FontAwesomeIcon icon={faCommentAltLines} style={{ fontSize: 16, color: '#94a3b8', marginTop: 3, flexShrink: 0 }} />
      <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, fontWeight: 400 }}>
        {response.text}
      </Typography>
    </Box>
  ));
};

ResponseList.propTypes = {
  responses: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      role: PropTypes.string,
      school: PropTypes.string,
    })
  ),
};

ResponseList.defaultProps = {
  responses: [],
};

const ANONYMITY_MESSAGE = 'To protect anonymity, results are hidden when fewer than 5 participants responded.';

const QuestionAccordion = React.memo(({ question }) => {
  const currentResponses = question.base?.responses || [];
  const comparisonResponses = question.comparison?.responses || [];
  const responses = comparisonResponses.length > 0 ? [...currentResponses, ...comparisonResponses] : currentResponses;
  const totalResponses = question.base?.total_responses ?? responses.length;
  const [scrolled, setScrolled] = React.useState(false);

  if (question.base?.suppressed) {
    return (
      <Box sx={{ borderTop: '1px solid #e2e8f0', px: 2.5, py: 2 }}>
        <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, fontWeight: 700 }}>
          {question.question}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', mt: '2px', fontWeight: 400 }}>
          {totalResponses} {totalResponses === 1 ? 'Response' : 'Responses'}
        </Typography>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 80, mt: 1 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b', fontSize: '0.875rem', lineHeight: 1.4, textAlign: 'center' }}>
            {ANONYMITY_MESSAGE}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        borderTop: '1px solid #e2e8f0',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
        sx={{
          px: 2.5,
          py: 1,
          borderBottom: `1px solid ${scrolled ? '#e2e8f0' : 'transparent'}`,
          transition: 'border-color 0.15s ease',
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, fontWeight: 700 }}>
            {question.question}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: '2px', fontWeight: 400 }}>
            {totalResponses} {totalResponses === 1 ? 'Response' : 'Responses'}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails onScroll={(e) => setScrolled(e.target.scrollTop > 0)} sx={{ px: 2.5, pt: 1, pb: 1.8, maxHeight: 380, overflowY: 'auto' }}>
        <ResponseList responses={responses} />
      </AccordionDetails>
    </Accordion>
  );
});

QuestionAccordion.displayName = 'QuestionAccordion';

QuestionAccordion.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string.isRequired,
    base: PropTypes.shape({
      responses: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          role: PropTypes.string,
          school: PropTypes.string,
        })
      ),
    }),
    comparison: PropTypes.shape({
      responses: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          role: PropTypes.string,
          school: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
};

const OpenTextSection = ({ sections, onExport, isExporting }) => {
  const allSuppressed = sections.length > 0 && sections.every((s) => s.questions.every((q) => q.base?.suppressed === true));
  const exportDisabled = isExporting || allSuppressed;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ position: 'relative' }} className="mb-3">
        <Typography variant="body1" sx={{ color: '#637a9a', letterSpacing: 1.3, fontWeight: 700, textTransform: 'uppercase' }}>
          Open Ended Questions
        </Typography>
        {onExport && (
          <Button
            size="small"
            disabled={allSuppressed}
            onClick={exportDisabled ? undefined : onExport}
            startIcon={
              isExporting ? (
                <CircularProgress size={14} sx={{ color: '#64748b' }} />
              ) : (
                <FontAwesomeIcon icon={faFileExport} style={{ fontSize: 13 }} />
              )
            }
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              color: '#64748b',
              fontWeight: 400,
              fontSize: '0.85rem',
              textTransform: 'none',
              px: 1,
              cursor: exportDisabled ? 'default' : 'pointer',
              opacity: allSuppressed ? 0.5 : 1,
              '&:hover': { backgroundColor: 'transparent', color: exportDisabled ? '#64748b' : '#334155' },
            }}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        )}
      </Box>
      {sections.length === 0 ? (
        <OpenTextEmptyState message="No responses found for the selected filters." />
      ) : (
        sections.map((domainSection) => (
          <Box
            key={domainSection.domain}
            sx={{
              border: '1px solid #cbd5e1',
              borderRadius: 2.2,
              overflow: 'hidden',
              mb: 3,
              backgroundColor: '#ffffff',
              boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.08)',
            }}
          >
            {domainSection.questions.map((question) => (
              <QuestionAccordion key={`${domainSection.domain}-${question.question_id}`} question={question} />
            ))}
            {domainSection.questions.length === 0 && (
              <Box sx={{ px: 2.5, py: 1.8 }}>
                <Typography variant="body1" sx={{ ...QUESTION_TEXT_SX, color: '#64748b' }}>
                  No responses found in this domain.
                </Typography>
              </Box>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};

OpenTextSection.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      domain: PropTypes.string.isRequired,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          question_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          question: PropTypes.string,
          base: PropTypes.shape({
            responses: PropTypes.arrayOf(
              PropTypes.shape({
                text: PropTypes.string.isRequired,
                role: PropTypes.string,
                school: PropTypes.string,
              })
            ),
          }),
          comparison: PropTypes.shape({
            responses: PropTypes.arrayOf(
              PropTypes.shape({
                text: PropTypes.string.isRequired,
                role: PropTypes.string,
                school: PropTypes.string,
              })
            ),
          }),
        })
      ).isRequired,
    })
  ).isRequired,
  onExport: PropTypes.func,
  isExporting: PropTypes.bool,
};

OpenTextSection.defaultProps = {
  onExport: null,
  isExporting: false,
};

export default OpenTextSection;
